"use client"

import { css } from "@emotion/css"
import styled from "@emotion/styled"
import React from "react"
import { useTranslation } from "react-i18next"

import { useCornerTapFlip } from "@/hooks/course-material/useCornerTapFlip"
import DefaultSVG from "@/shared-module/common/img/hero-default-bg-image.svg"
import { baseTheme } from "@/shared-module/common/styles"
import { respondToOrLarger } from "@/shared-module/common/styles/respond"
import { Button } from "@/shared-module/components"
import { COURSE_MATERIAL_DEFAULT_BLOCK_MARGIN_REM } from "@/utils/course-material/constants"
import { escapeUrlForCss } from "@/utils/course-material/sanitizeCourseMaterialHtml"

import {
  type HeroBackgroundImages,
  heroTextReadabilityCss,
  resolveHeroBackgroundImages,
} from "./heroBackgroundImages"
import ParsedText from "./ParsedText"

export const CHAPTER_GRID_SCROLLING_DESTINATION_CLASSNAME_DOES_NOT_AFFECT_STYLING =
  "chapter-grid-scrolling-destination"

interface TextBoxProps {
  fontColor?: string
  backgroundImages: HeroBackgroundImages
}

const TextBox = styled.div<TextBoxProps>`
  display: flex;
  flex-direction: column;
  padding: 2rem 2.5rem;
  margin-bottom: 1rem;
  align-items: center;
  text-align: center;
  justify-content: center;
  ${({ backgroundImages }) => heroTextReadabilityCss(backgroundImages.mobile)}

  ${respondToOrLarger.md} {
    ${({ backgroundImages }) => heroTextReadabilityCss(backgroundImages.medium)}
  }

  ${respondToOrLarger.lg} {
    ${({ backgroundImages }) => heroTextReadabilityCss(backgroundImages.large)}
  }

  ${respondToOrLarger.xl} {
    ${({ backgroundImages }) => heroTextReadabilityCss(backgroundImages.xlarge)}
  }

  h1 {
    z-index: 20;
    margin-bottom: 0.8rem;
    color: ${({ color }) => (color ? color : baseTheme.colors.gray[700])};
    margin-top: 1.5rem;

    font-weight: bold;
    max-width: 100%;
    line-height: 1.1;

    font-size: clamp(1.3rem, 4vw, 60px);
    ${respondToOrLarger.xxxs} {
      font-size: clamp(1.5rem, 4vw, 60px);
    }

    ${respondToOrLarger.xxs} {
      font-size: clamp(2rem, 4vw, 60px);
    }

    ${respondToOrLarger.xs} {
      font-size: clamp(2.4rem, 4vw, 60px);
    }

    ${respondToOrLarger.md} {
      width: 50vw;
    }
  }

  .hero-subtitle {
    width: 100%;
    color: ${({ color }) => (color ? color : baseTheme.colors.gray[700])};
    ${respondToOrLarger.md} {
      width: 600px;
    }
  }

  button {
    margin-top: 2rem;
    text-align: center;
    font-weight: 700;
  }
`

const StyledSVG = styled(DefaultSVG)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 9;
`
export interface LandingPageHeroSectionProps {
  title: string
  backgroundImage?: string
  backgroundImageMedium?: string
  backgroundImageLarge?: string
  backgroundImageXLarge?: string
  backgroundColor?: string
  fontColor?: string
  backgroundRepeatX?: boolean
  variant?: string
}

export type CardProps = React.HTMLAttributes<HTMLDivElement> & LandingPageHeroSectionProps

const LandingPageHeroSection: React.FC<React.PropsWithChildren<CardProps>> = ({
  title,
  children,
  backgroundImage,
  backgroundImageMedium,
  backgroundImageLarge,
  backgroundImageXLarge,
  backgroundColor,
  backgroundRepeatX,
  fontColor,
}) => {
  const { t } = useTranslation()
  const { containerRef, onPointerDown, flipClassName } = useCornerTapFlip()

  const backgroundImages = resolveHeroBackgroundImages({
    backgroundImage,
    backgroundImageMedium,
    backgroundImageLarge,
    backgroundImageXLarge,
  })

  return (
    <div
      ref={containerRef}
      onPointerDown={onPointerDown}
      className={css`
        position: relative;
        overflow: hidden;
        width: 100%;
        border-radius: 1px;
        transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
        padding: 5em 1em;
        margin-top: -${COURSE_MATERIAL_DEFAULT_BLOCK_MARGIN_REM}rem;
        background-color: ${backgroundColor};
        ${
          backgroundImages.mobile &&
          `background-image: url("${escapeUrlForCss(backgroundImages.mobile)}");
        background-repeat: ${backgroundRepeatX ? "repeat-x" : "no-repeat"};
        background-position: center center;`
        }
        background-size: cover;
        touch-action: manipulation;

        ${respondToOrLarger.md} {
          ${
            backgroundImages.medium &&
            `background-image: url("${escapeUrlForCss(backgroundImages.medium)}");`
          }
        }

        ${respondToOrLarger.lg} {
          ${
            backgroundImages.large &&
            `background-image: url("${escapeUrlForCss(backgroundImages.large)}");`
          }
        }

        ${respondToOrLarger.xl} {
          ${
            backgroundImages.xlarge &&
            `background-image: url("${escapeUrlForCss(backgroundImages.xlarge)}");`
          }
        }

        ${respondToOrLarger.xxxxl} {
          background-size: auto;
        }
      `}
    >
      {backgroundImages.mobile === undefined && <StyledSVG />}
      <TextBox backgroundImages={backgroundImages} color={fontColor}>
        <ParsedText
          text={title}
          tag="h1"
          tagProps={{ className: flipClassName }}
          useWrapperElement={true}
        />
        <div className="hero-subtitle">{children}</div>
        <Button
          variant="primary"
          size="large"
          className={css`
            z-index: 99 !important;
          `}
          onClick={() => {
            const firstChapterGrid = document.querySelector(
              `.${CHAPTER_GRID_SCROLLING_DESTINATION_CLASSNAME_DOES_NOT_AFFECT_STYLING}`,
            )
            if (!firstChapterGrid) {
              console.warn("Cannot find scroll destination")
              return
            }
            // oxlint-disable-next-line i18next/no-literal-string
            firstChapterGrid.scrollIntoView({ behavior: "smooth" })
          }}
        >
          {t("start-course")}
        </Button>
      </TextBox>
    </div>
  )
}

export default LandingPageHeroSection
